import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Input, Modal, Radio, Select, Spin, Tag } from "antd";
import RolePermissionsMatrix from "./RolePermissionsMatrix";
import { buildPermissionIdsFromRole, getRoleOwnershipBadge } from "./rolesPermissionsUtils";

const emptyCreateForm = {
  nombre: "",
  descripcion: "",
  idclinica: null,
  creationSource: "empty", // "empty" | "copy"
  sourceRoleId: null,
};

// RBAC-CUSTOM-ROLES-03B seccion G/I/J/K/O: formulario unico para 2 flujos
// bien distintos --
//   mode="create": SIEMPRE crea un rol PERSONALIZADO por clinica (unico
//   flujo expuesto por "+ Nuevo rol"/"Copiar", gateado por
//   roles.custom.create). El backend fuerza is_system/scope_type/created_by
//   -- este formulario NUNCA los envia como eleccion del usuario.
//   mode="edit": SOLO metadata de un rol YA personalizado -- nombre y
//   descripcion; idclinica/scope_type/is_system/created_by nunca editables
//   desde UI.
export default function RoleForm({
  open,
  mode,
  role,
  roles,
  authorizedClinics,
  clinicsError,
  catalog,
  fetchRoleDetail,
  initialCopySource,
  onCancel,
  onSubmit,
  loading,
  error,
}) {
  const [form, setForm] = useState(emptyCreateForm);
  const [nombreEdit, setNombreEdit] = useState("");
  const [descripcionEdit, setDescripcionEdit] = useState("");
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceError, setSourceError] = useState("");

  // RBAC-CUSTOM-ROLES-03B: reset SOLO al transicionar a abierto -- NUNCA
  // depende de `authorizedClinics` (llega asincronamente despues de abrir,
  // ver `loadAuthorizedClinics` en RolesIndex.js). Si estuviera en las
  // dependencias, el formulario se reiniciaria (borrando nombre/descripcion
  // ya escritos) justo cuando termina de cargar la lista de clinicas.
  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && role) {
      setNombreEdit(role.nombre || "");
      setDescripcionEdit(role.descripcion || "");
      return;
    }
    // mode === "create"
    if (initialCopySource) {
      setForm({ ...emptyCreateForm, creationSource: "copy", sourceRoleId: initialCopySource.idrol });
    } else {
      setForm({ ...emptyCreateForm });
    }
    setSelectedPermissionIds([]);
    setSourceError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, role, initialCopySource]);

  // RBAC-CUSTOM-ROLES-03B seccion H: preseleccion de la UNICA clinica
  // autorizada -- efecto SEPARADO e idempotente (solo toca `idclinica`,
  // nunca el resto del formulario) que actua en cuanto `authorizedClinics`
  // termina de cargar, sin pisar una eleccion que el usuario ya haya hecho.
  useEffect(() => {
    if (!open || mode !== "create") return;
    if ((authorizedClinics || []).length !== 1) return;
    setForm((current) => (current.idclinica === null ? { ...current, idclinica: authorizedClinics[0].idclinica } : current));
  }, [open, mode, authorizedClinics]);

  // RBAC-CUSTOM-ROLES-03B seccion J/K: al elegir un rol fuente (incluye
  // roles Sistema), se lee su detalle (solo lectura, GET /roles/:idrol) y se
  // precarga la matriz con una copia INDEPENDIENTE de sus permission_ids --
  // desmarcar aqui en adelante SOLO muta este estado local del formulario,
  // nunca el rol fuente (no se llama ningun PUT/DELETE sobre el).
  useEffect(() => {
    if (mode !== "create" || form.creationSource !== "copy" || !form.sourceRoleId) {
      if (mode === "create" && form.creationSource === "empty") setSelectedPermissionIds([]);
      return;
    }
    let cancelado = false;
    (async () => {
      setSourceLoading(true);
      setSourceError("");
      try {
        const detail = await fetchRoleDetail(form.sourceRoleId);
        if (!cancelado) setSelectedPermissionIds(buildPermissionIdsFromRole(detail));
      } catch (err) {
        if (!cancelado) {
          setSourceError(err?.response?.data?.message || err?.message || "No se pudo cargar el rol base");
          setSelectedPermissionIds([]);
        }
      } finally {
        if (!cancelado) setSourceLoading(false);
      }
    })();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, form.creationSource, form.sourceRoleId]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = () => {
    if (mode === "edit") {
      onSubmit(role?.is_system ? { descripcion: descripcionEdit } : { nombre: nombreEdit, descripcion: descripcionEdit });
      return;
    }
    onSubmit({
      nombre: form.nombre,
      descripcion: form.descripcion,
      idclinica: form.idclinica,
      // RBAC-CUSTOM-ROLES-03A: el backend IGNORA/fuerza este valor a CLINIC
      // para todo rol con `idclinica` presente -- se envia solo para
      // satisfacer el schema del body (`required: ['nombre','scope_type']`).
      scope_type: "CLINIC",
      permission_ids: selectedPermissionIds,
    });
  };

  const canSubmitCreate =
    mode === "create" &&
    form.nombre.trim().length > 0 &&
    form.idclinica !== null &&
    (form.creationSource === "empty" || (form.creationSource === "copy" && form.sourceRoleId));

  return (
    <Modal
      title={mode === "create" ? "Nuevo rol" : "Editar rol"}
      open={open}
      onCancel={onCancel}
      width={mode === "create" ? 760 : 520}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          disabled={mode === "create" && !canSubmitCreate}
          onClick={submit}
        >
          Guardar
        </Button>,
      ]}
      destroyOnHidden
    >
      {error && <Alert className="mb-3" type="error" message={error} showIcon />}

      {mode === "edit" ? (
        <Form layout="vertical">
          <Form.Item label="Nombre">
            <Input
              value={nombreEdit}
              disabled={role?.is_system}
              onChange={(event) => setNombreEdit(event.target.value)}
            />
          </Form.Item>
          {role?.clinica_nombre && (
            <Form.Item label="Clínica">
              <Tag color="green">{role.clinica_nombre}</Tag>
            </Form.Item>
          )}
          <Form.Item label="Descripción">
            <Input.TextArea
              rows={3}
              value={descripcionEdit}
              onChange={(event) => setDescripcionEdit(event.target.value)}
            />
          </Form.Item>
        </Form>
      ) : (
        <Form layout="vertical">
          <Form.Item label="Nombre" required>
            <Input value={form.nombre} onChange={(event) => update("nombre", event.target.value)} />
          </Form.Item>

          <Form.Item label="Descripción">
            <Input.TextArea
              rows={2}
              value={form.descripcion}
              onChange={(event) => update("descripcion", event.target.value)}
            />
          </Form.Item>

          <Form.Item label="Clínica" required>
            {clinicsError ? (
              <Alert type="warning" message={clinicsError} showIcon />
            ) : (
              <Select
                placeholder="Seleccione la clínica propietaria del rol"
                value={form.idclinica}
                onChange={(value) => update("idclinica", value)}
                options={(authorizedClinics || []).map((clinica) => ({
                  value: clinica.idclinica,
                  label: clinica.nombre,
                }))}
                notFoundContent="No tiene clínicas autorizadas"
              />
            )}
          </Form.Item>

          <Form.Item label="Crear desde">
            <Radio.Group
              value={form.creationSource}
              onChange={(event) => update("creationSource", event.target.value)}
            >
              <Radio value="empty">Vacío</Radio>
              <Radio value="copy">Copiar rol existente</Radio>
            </Radio.Group>
          </Form.Item>

          {form.creationSource === "copy" && (
            <Form.Item label="Rol base" required>
              <Select
                placeholder="Seleccione el rol a copiar"
                value={form.sourceRoleId}
                onChange={(value) => update("sourceRoleId", value)}
                options={(roles || []).map((item) => ({
                  value: item.idrol,
                  label: `${item.nombre} [${getRoleOwnershipBadge(item)}]${item.clinica_nombre ? ` - ${item.clinica_nombre}` : ""}`,
                }))}
              />
            </Form.Item>
          )}

          {sourceError && <Alert className="mb-3" type="error" message={sourceError} showIcon />}

          <Form.Item label="Permisos">
            {sourceLoading ? (
              <Spin />
            ) : (
              <RolePermissionsMatrix
                catalog={catalog}
                selectedIds={selectedPermissionIds}
                readOnly={false}
                hideSaveButton
                error=""
                onChange={setSelectedPermissionIds}
              />
            )}
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}
